import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { to55 } from '@/lib/operadoraCoerencia';

interface ParqueMovelItem {
  id: string;
  cnpj: string;
  nr_telefone: string;
  cliente: string;
  plano: string;
  fat_medio_03_meses: number | null;
  m: number | null;
  qtd_gb_contratado_dados: number | null;
  qtd_gb_traf_dados: number | null;
  aparelho_trafego_marca: string | null;
  aparelho_trafego_modelo: string | null;
  fidelizado: string | null;
  // 🆕 Inteligência da base (ótica Claro)
  dt_primeita_ativacao: string | null;
  qt_planta: number | null;
  semaforo_serasa: string | null;
  mensagem_retorno_serasa: string | null;
  situacao_receita: string | null;
  tem_cobertura_banda_larga: string | null;
  tem_linha_fixa: string | null;
  cliente_apto_convergencia: string | null;
  uf: string | null;
  ds_municipio: string | null;
  nr_cep: string | null;
}

interface ParqueFixoItem {
  id: string;
  cnpj: string;
  conta_cobranca: string;
  ds_produto: string;
  tecnologia_origem: string | null;
  vl_fat_bruto: number | null;
  m: number | null;
  designador: string | null;
  ds_tipo_produto: string | null;
  // Campos de endereço
  endereco: string | null;
  numero: string | null;
  bairro: string | null;
  ds_municipio_origem: string | null;
  uf: string | null;
  cep: string | null;
  // Novos campos
  rede: string | null;
  dt_ini_fdlz: string | null;
  dt_fim_fdlz: string | null;
  ds_tipo_rede_2: string | null;
  depara: string | null;
  flg_sva: number | null;
  ds_produto_tecnologia: string | null;
  flg_recomendacao: string | null;
  complemento: string | null;
  ds_end_prefixo: string | null;
}

export function useParqueMovelByCnpj(cnpj: string | null | undefined) {
  return useQuery({
    queryKey: ['parque-movel-cnpj', cnpj],
    queryFn: async (): Promise<ParqueMovelItem[]> => {
      if (!cnpj) return [];
      const cnpjLimpo = cnpj.replace(/\D/g, '');
      
      const { data, error } = await supabase
        .from('staging_parque_movel')
        .select('id, cnpj, nr_telefone, cliente, plano, fat_medio_03_meses, m, qtd_gb_contratado_dados, qtd_gb_traf_dados, aparelho_trafego_marca, aparelho_trafego_modelo, fidelizado, dt_carga, dt_primeita_ativacao, qt_planta, semaforo_serasa, mensagem_retorno_serasa, situacao_receita, tem_cobertura_banda_larga, tem_linha_fixa, cliente_apto_convergencia, uf, ds_municipio, nr_cep')
        .eq('cnpj', cnpjLimpo)
        .order('fat_medio_03_meses', { ascending: false, nullsFirst: false });
      
      if (error) {
        console.error('Erro ao buscar parque móvel:', error);
        return [];
      }
      
      return (data || []) as ParqueMovelItem[];
    },
    enabled: !!cnpj,
    staleTime: 5 * 60 * 1000,
  });
}

export function useParqueFixoByCnpj(cnpj: string | null | undefined) {
  return useQuery({
    queryKey: ['parque-fixo-cnpj', cnpj],
    queryFn: async (): Promise<ParqueFixoItem[]> => {
      if (!cnpj) return [];
      const cnpjLimpo = cnpj.replace(/\D/g, '');
      
      const { data, error } = await supabase
        .from('staging_parque_fixo')
        .select('id, cnpj, conta_cobranca, ds_produto, tecnologia_origem, vl_fat_bruto, m, designador, ds_tipo_produto, endereco, numero, bairro, ds_municipio_origem, uf, cep, rede, dt_ini_fdlz, dt_fim_fdlz, ds_tipo_rede_2, depara, flg_sva, ds_produto_tecnologia, flg_recomendacao, complemento, ds_end_prefixo')
        .eq('cnpj', cnpjLimpo)
        .order('vl_fat_bruto', { ascending: false, nullsFirst: false });
      
      if (error) {
        console.error('Erro ao buscar parque fixo:', error);
        return [];
      }
      
      return (data || []) as ParqueFixoItem[];
    },
    enabled: !!cnpj,
    staleTime: 5 * 60 * 1000,
  });
}

// 🆕 Cobertura Claro (BL) pelo CEP — cruzamento Vivo x Claro
export interface CoberturaClaro {
  coberto: boolean;
  cep: string;
  tipo_servico: string | null;
  velocidade_max_mbps: number | null;
}

export function useCoberturaClaroByCep(cep: string | null | undefined) {
  const cepLimpo = (cep || '').replace(/\D/g, '');
  const enabled = cepLimpo.length === 8;
  return useQuery({
    queryKey: ['cobertura-claro-cep', cepLimpo],
    queryFn: async (): Promise<CoberturaClaro> => {
      const { data, error } = await supabase
        .from('cep_cobertura_claro_bl')
        .select('cep, tipo_servico, velocidade_max_mbps, ativo')
        .eq('cep', cepLimpo)
        .eq('ativo', true)
        .order('velocidade_max_mbps', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error('[useCoberturaClaroByCep] Erro:', error);
        return { coberto: false, cep: cepLimpo, tipo_servico: null, velocidade_max_mbps: null };
      }
      return {
        coberto: !!data,
        cep: cepLimpo,
        tipo_servico: (data as any)?.tipo_servico ?? null,
        velocidade_max_mbps: (data as any)?.velocidade_max_mbps ?? null,
      };
    },
    enabled,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * A operadora ATUAL de cada linha da base Vivo, lendo só o cache.
 *
 * A base diz que a linha é Vivo NA DATA DA CARGA. Isto diz quem ela é AGORA —
 * várias já foram portadas, inclusive por nós. Sem isto o consultor abre a aba,
 * vê tudo como Vivo, e oferece portabilidade para quem já é Claro.
 *
 * ⚠️ POR QUE NÃO É O `useOperadoraLinhas` QUE JÁ EXISTE:
 * aquele serve à régua produto × operadora do Montador e, para o que não está
 * em cache, CHAMA A API AO VIVO em lotes de 10. Ali são os poucos números que
 * a pessoa digitou. Aqui é o parque inteiro de um CNPJ — e tem CNPJ com 330
 * linhas, o que viraria 33 chamadas só por abrir uma aba. Este lê o cache e
 * pronto: quem preenche o cache é a varredura semanal.
 * Ele também traz o que o outro não traz: a DATA da portabilidade.
 *
 * O `to55` é o mesmo dos dois — normalizar telefone em dois lugares diferentes
 * é como se perde a conta (o parque guarda 11 dígitos e telefones_operadora
 * guarda 13, com o 55; foi essa diferença que fez a primeira medição dar "zero
 * linhas consultadas" quando eram 248.657).
 */
export function useOperadoraDasLinhas(telefones: (string | null | undefined)[]) {
  const nums = Array.from(new Set((telefones || []).map((t) => to55(String(t ?? ''))).filter(Boolean))) as string[];
  return useQuery({
    queryKey: ['operadora-das-linhas', nums.join(',')],
    enabled: nums.length > 0,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const { data } = await supabase
        .from('telefones_operadora')
        .select('telefone, operadora, ultima_consulta, metadata')
        .in('telefone', nums);
      const mapa = new Map<string, { operadora: string | null; consultadaEm: string | null; portouEm: string | null }>();
      type LinhaOperadora = {
        telefone: string;
        operadora: string | null;
        ultima_consulta: string | null;
        metadata: { data_portabilidade?: string } | null;
      };
      for (const r of ((data ?? []) as unknown as LinhaOperadora[])) {
        const dp = String(r.metadata?.data_portabilidade ?? '');
        mapa.set(String(r.telefone), {
          operadora: r.operadora ?? null,
          consultadaEm: r.ultima_consulta ?? null,
          // '0000-00-00' é o "sem portabilidade" da API — não é data
          portouEm: dp && !dp.startsWith('0000') ? dp : null,
        });
      }
      return mapa;
    },
  });
}

/** true quando a consulta diz que a linha NÃO é mais Vivo. Nunca consultada
 *  devolve false: não saber não é o mesmo que ter saído. */
export function saiuDaVivo(op?: string | null): boolean {
  if (!op) return false;
  const o = op.toUpperCase();
  if (o.startsWith('VIVO')) return false;
  return !['DESCONHECIDO', 'NAO LOCALIZADO', 'NÃO LOCALIZADO'].includes(o);
}
